/**
 * @classdesc
 * Attribute template model
 *
 * @property {reference} attributeTypes - Available search types
 * @property {number} attributeTypes.UNKNOWN      - 0
 * @property {number} attributeTypes.TEXT         - 1
 * @property {number} attributeTypes.TEXTAREA     - 2
 * @property {number} attributeTypes.PULLDOWN     - 3  (select)
 * @property {number} attributeTypes.DATE         - 4
 * @property {number} attributeTypes.COMBO        - 5  (multiselect)
 * @property {number} attributeTypes.TAGS         - 6
 * @property {number} attributeTypes.NUMERIC      - 7
 * @property {number} attributeTypes.HIERARCHICAL - 8  (deprecated)
 * @property {number} attributeTypes.TAGSET       - 9  (deprecated)
 * @property {number} attributeTypes.AUTOCOMPLETE - 10 (deprecated)
 * @property {number} attributeTypes.DATALOOKUP   - 11 (deprecated)
 * @property {number} attributeTypes.SEMANTIC     - 12 (deprecated)
 *
 * @class
 * @extends Netx.Models.PermissionResource
 */
import {attributeHistoryEnabled} from '@netx/core-attribute-history/enablers';
import {
	ATTRIBUTE_EDITABLE_TYPES,
	ATTRIBUTE_FACET_TYPES,
	ATTRIBUTE_MULTIPLE_VALUE_DEPRECATED_TYPES,
	ATTRIBUTE_NUMBER_SPEC_2_TYPE,
	AttributeType,
} from '@netx/core-attribute/constants';
import {getLabel} from '@netx/core-attribute/utils/getLabel';
import {getTypeLabel} from '@netx/core-attribute/utils/getTypeLabel';
import {getTypeName} from '@netx/core-attribute/utils/getTypeName';
import Base from '@netx/core-permissions/models/permissionResource';
import {getPreference} from '@netx/core-user-next/utils/getPreference';
import {escapeAttributeName} from '@netx/core/lib/underscore-netx';

export default class Attribute extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/** @type {string} */
	get idAttribute() {
		// DEVNOTE: the capitalization of 'id' is particularly haphazard in this object.
		// groupIds is capital I, the others are all lowercase i.  This is mirroring the
		// object on the server ... which should be fixed!
		// TODO: could we regularize that on the client side, or would be there be trouble?
		return 'attributeid';
	}
	/** @override */
	get titleAttribute() {
		return 'name';
	}

	/**
	 * @property {object}  defaults                      - The default attribute values
	 * @property {number}  defaults.attributeid          - ID (someone has explicitely set this to 0 without any reasoning - the `isNew` method has been adjusted accordingly. https://confluence.netx.net/display/DEV/Avoid+setting+default+IDs+in+X7+models)
	 * @property {array}   defaults.groupIds             - Group ids (Unknown use - possibly permission oriented, and should be deprecated with the new permission system)
	 * @property {boolean} defaults.facet                - Is this attribute a faceted attribute
	 * @property {boolean} defaults.hasPermissions       - Does this attribute have permission records?
	 * @property {number}  defaults.imageid              - Image id (Unknown use)
	 * @property {array}   defaults.keyDatasets          - Datasets attribute is tied to
	 * @property {number}  defaults.mandatory            - Mandatory value (boolean as number)
	 * @property {string}  defaults.modDate              - Modification date
	 * @property {boolean} defaults.multivalue           - Multivalue attribute
	 * @property {string}  defaults.name                 - Attribute name
	 * @property {array}   defaults.objectTypes          - Object types (asset/clip)
	 * @property {number}  defaults.orderby              - Order by (auto-increments when attribute is added - currently we do not respect this, we order the collection by `name`)
	 * @property {boolean} default.permissioned			 - New permission system enabled
	 * @property {array}   defaults.pulldownOptions      - Pulldown options (for select/multiselect)
	 * @property {number}  defaults.restrictedUserLevel  - Restricted user level (Should be deprecated with the new permission system)
	 * @property {number}  defaults.semanticTypeId       - Semantic type id (only used if `type` is 12 - semantic)
	 * @property {number}  defaults.spec1                - Use dependent on the attribute type ( number - format, tag - dateSet id )
	 * @property {number}  defaults.spec2                - Use dependent on the attribute type
	 * @property {array}   defaults.targetDatasets       - Ids of datasets that use this attribute as the key for a metadata lookup
	 * @property {number}  defaults.templateid           - Attribute template id (the exact same as the `attributeid` - must be legacy code)
	 * @property {number}  defaults.type                 - Attribute type (as defined in module:asset/properties/attributes#types)
	 * @property {boolean} defaults.trackHistory         - Track attribute history (must have `metadata.history.enabled`)
	 * @property {number}  defaults.userid               - ID of creating user
	 * @property {mixed}   defaults.value                - Default attribute value
	 *
	 * @property {object}  defaults.attributeOptions     - Attribute options for fields
	 */
	get defaults() {
		return {
			attributeid: 0,
			groupIds: [],
			facet: false,
			hasPermissions: true,
			imageid: 0,
			keyDatasets: [],
			objectTypes: ['asset'],
			mandatory: 0,
			modDate: '',
			multivalue: false,
			name: '',
			orderby: 0,
			permissioned: false,
			pulldownOptions: [],
			restrictedUserLevel: 1,
			semanticTypeId: 0,
			spec1: 0,
			spec2: 0,
			targetDatasets: [],
			templateid: 0,
			trackHistory: false,
			type: 1,
			userid: 0,
			value: '',

			// Client attributes
			// For things like upload asset request to get their custom labels/placeholders/captions to the view
			// rendering the attribute field
			attributeOptions: {},
			vocabulary: null,
		};
	}

	/** @type {array} */
	get clientAttrs() {
		return ['attributeOptions', 'vocabulary'];
	}

	/**
	 * @property {object} methods                        - Methods for this model
	 * @property {method} methods.metadataDatasetLookup  - Metadata dataset lookup
	 */
	get methods() {
		return {
			remove: ['deleteAttribute', 'attributeid'],
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			create(opts) {
				return ['setAttributeObject', this.self()];
			},
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			update(opts) {
				return ['setAttributeObject', this.self()];
			},
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			read(opts) {
				return ['getAttributeObjects', [this.get('attributeid')]];
			},

			/**
			 * @method
			 * @param {object} opts Request options
			 */
			getAttributeVocabulary: (opts) => {
				return ['getAttributeVocabulary', this.get('spec1')];
			},
			/**
			 * @method
			 * @param {string} val Value
			 * @param {object} opts Request options
			 */
			metadataDatasetLookup(val, opts) {
				return ['metadataDatasetLookup', this.get('name'), val];
			},
		};
	}

	/**
	 * @property {object} parsers                        - Parsers for this model
	 * @property {method} parsers.metadataDatasetLookup  - Parse response of metadata dataset lookup
	 */
	get parsers() {
		return {
			/**
			 * We do not have a call specifically to fetch a single record
			 * But we can pass ids and get a single record back wrapped in an array
			 * @function
			 * @param {array} data Attribute data
			 * @param {object} opts Request options
			 */
			read(data, opts) {
				return _.isArray(data) && data.length ? data[0] : null;
			},
			/**
			 * @function
			 * @param {object} data Vocabulary
			 * @param {object} opts Request options
			 */
			getAttributeVocabulary(data, opts) {
				if (!data) return;
				this.set('vocabulary', data);
			},
			/**
			 * @function
			 * @param {array} match Matches
			 * @param {object} opts Request options
			 */
			metadataDatasetLookup(matches, opts) {
				// This API call sometimes returns nulls
				// Some csvs can contain empty label/values as well
				return _.filter(matches, (match) => match && match.label !== '');
			},
		};
	}

	/**
	 * @override
	 * @param {object} attrs Model attributes
	 * @param {object} opts Model options
	 */
	constructor(attrs, opts = {}) {
		/**
		 * Shared resource type name
		 * @type {string}
		 */
		opts.resourceName = 'attribute';

		super(attrs, opts);

		/**
		 * Though multiselects/tags/semantics are multivalue, they are not dealt with the same
		 * @memberof Netx.Models.Attribute
		 * @alias multivalueTypes
		 * @type {array}
		 */
		this.multivalueTypes = [1, 2, 3, 4, 7];

		/**
		 * multiselects/tags/semantics arenative multivalues, but they are not dealt with the same as other mutlivalues
		 * @memberof Netx.Models.Attribute
		 * @alias multivalueTypes
		 * @type {array}
		 */
		this.nativeMultivalueTypes = [5, 6, 12, 13];

		// Hey, listen!
		this.listenTo(dispatcher, 'system:attribute:edit:success', (model) => {
			// See Netx.Models.AttributeData for the reasoning behind the `templateId` check
			if (
				(model.id === this.id || model.id === this.get('templateId')) &&
				model !== this
			) {
				this.set(
					_.clone(_.omit(model.attributes, 'attributeid', 'templateid')),
				);
			}
		})
			// Listen for permission changes and refetch the attribute model
			.listenTo(
				dispatcher,
				'permissions:permission:edit:success permissions:permission:delete:success',
				(permission) => {
					const resource = permission.get('resource');
					if (resource.type === 'attribute' && resource.id === this.id) {
						this.fetch();
					}
				},
			);
	}

	/**
	 * Add object type to attribute
	 * @method
	 * @param {string} objectType Object type (asset/clip)
	 * @type {object} opts Request options
	 */
	addObjectType(objectType, opts) {
		const objectTypes = this.get('objectTypes');

		if (_.contains(objectTypes, objectType)) {
			return new $.Deferred().resolve();
		}

		return this.save(
			{objectTypes: objectTypes.concat([objectType])},
			_.extend({wait: true}, opts),
		);
	}

	/**
	 * For when an attribute template model has been amalgamated with an attribute data model
	 * @method
	 * @return {boolean}
	 * @see Netx.Models.AttributeData
	 */
	allows(perm) {
		if (perm == null) {
			return undefined;
		}

		if (!this.has('permission')) {
			return true;
		}

		const permission = this.get('permission');
		switch (perm) {
			case 'delete':
				return permission === 'd';
			case 'edit':
			case 'write':
				return permission === 'd' || permission === 'w';
			case 'read':
				return permission === 'd' || permission === 'w' || permission === 'r';
			default:
				return undefined;
		}
	}

	/**
	 * @method
	 * @param {mixed} value Value
	 * @param {number} truncateAt Truncate at index
	 * @return {mixed}
	 */
	format(value, truncateAt) {
		value = this.parseInValue(value);

		if (_.isArray(value)) {
			value = value.join(', ');
		}

		if (typeof value === 'string') {
			value = value.replace(/</g, '&lt;');
			value = value.replace(/>/g, '&gt;');
		}

		if (value != null && truncateAt && value.length > truncateAt) {
			value = value.substr(0, truncateAt) + '...';
		}
		return value;
	}

	/**
	 * @method
	 * @return {object} Attribute options
	 */
	getAttributeOptions() {
		const attrOptions = {...(this.get('attributeOptions') || {})};
		if (attrOptions.required != null) {
			attrOptions.required = attrOptions.required === true;
		}

		if (!attrOptions.caption) delete attrOptions.caption;
		if (!attrOptions.placeholder) delete attrOptions.placeholder;

		return attrOptions;
	}

	/** @override */
	getLabel(dontEscape = false) {
		return getLabel(this.attributes, dontEscape === false);
	}

	/**
	 * Return the option values of an attribute pulldown
	 * @method
	 * @return {array} Pulldown values
	 */
	getOptions() {
		return this.get('pulldownOptions');
	}

	/**
	 * @method
	 * @return camelCased name without special characters
	 */
	getSafeName() {
		return _.toCamelCase(this.get('name'));
	}

	/**
	 * Get attribute sort field name
	 * User defined attributes are passed as they are named but system attributes are different
	 * ie: Modification Date -> moddate
	 * @method
	 * @param {boolean} escape Escape attribute name (makes it safe for data attrs etc)
	 * @return {string}
	 */
	getSortFieldName(escape) {
		const name = this.get('name');
		return escape ? escapeAttributeName(name) : name;
	}

	/**
	 * Get attribute english name from attribute type id
	 * @method
	 * @param {number}
	 * @return {string} Attribute type english name
	 * @todo Rename to getEnglis
	 */
	getTypeLabel(type) {
		return getTypeLabel(type ?? this.get('type'));
	}

	/**
	 * Get attribute type name from number
	 * @method
	 * @param {number} type Type id (if not provided this.attributes.type will be used)
	 * @return {string} Attribute name
	 */
	getTypeName(type) {
		return getTypeName(type ?? this.get('type'));
	}

	/**
	 * For when an attribute template model has been amalgamated with an attribute data model
	 * @method
	 * @return {array|mixed} Value(s)
	 * @see Netx.Models.AttributeData
	 */
	getValue() {
		if (!this.has('values')) return;

		// Is not a multivalue type attribute
		if (!this.get('multivalue')) return this.get('values')[0] || '';

		return this.get('values');
	}

	/**
	 * History enabled
	 * @method
	 * @return {boolean}
	 */
	historyEnabled() {
		return attributeHistoryEnabled();
	}

	/**
	 * @method
	 * @return {boolean} Has default value
	 */
	hasDefaultValue() {
		return this.get('value') != null && this.get('value') !== '';
	}

	/**
	 * @method
	 * @param {mixed} value Value
	 * @return {boolean} Invalid
	 */
	invalid(value) {
		return this.validator(value) !== true;
	}

	/**
	 * @method
	 * @return {boolean} Value is appendable
	 */
	isAppendable() {
		return false;
	}

	/**
	 * isFlexible() returns true if this attribute is defined as having 'folksonomy'
	 * and will therefore allow users to add terms via the select2 tagging system.
	 * @method
	 * @return {boolean} Is flexible
	 */
	isFlexible() {
		return false;
	}

	/**
	 * Test whether this attribute can be valid for a given object type, by
	 * checking the objectTypes array
	 * @method
	 * @param {string} objectType
	 * @return {boolean}
	 */
	isForObjectType(objectType) {
		return this.get('objectTypes').includes(objectType);
	}

	/**
	 * @method
	 * @return {boolean}
	 */
	isMandatory() {
		return this.get('mandatory') === 1;
	}

	/**
	 * We set the default id to 0
	 * @override
	 * @return {boolean} Is new
	 * @todo Someone explain why we have to set attributeid to 0 by default?
	 */
	isNew() {
		return !this.has(this.idAttribute) || this.id === 0;
	}

	/**
	 * Are we a native multivalue attribute (see `this.nativeMultivalueTypes`)
	 * @method
	 * @param {number} type Type
	 * @return {boolean}
	 */
	isNativeMultivalue(type) {
		type = (type && parseInt(type)) || this.get('type');
		return this.nativeMultivalueTypes.includes(type);
	}

	/**
	 * Are we a true multivalue attribute (see `this.multivalueTypes`)
	 * @method
	 * @return {boolean}
	 */
	isTrueMultivalue() {
		return (
			this.get('multivalue') &&
			ATTRIBUTE_MULTIPLE_VALUE_DEPRECATED_TYPES.includes(this.get('type'))
		);
	}

	/**
	 * Can values be written to for this attribute template
	 * @method
	 * @return {boolean}
	 */
	isValueWritable() {
		return !(
			this.get('system') ||
			this.get('readOnly') ||
			!ATTRIBUTE_EDITABLE_TYPES.includes(this.get('type'))
		);
	}

	/**
	 * Try to perform an auto-complete of attributes (DAM-6749)
	 * @method
	 */
	metadataLookup(value) {
		if (!this.get('keyDatasets').length) return new $.Deferred().resolve([]);

		return this.metadataDatasetLookup(value, {
			duplicateQueryPolicy: 'abort',
		}).then((matches) => {
			return _.compact(matches);
		});
	}

	/**
	 * @method
	 * @param {object} data Data
	 * @param {object} opts Request options
	 */
	parseAndSave(data, opts) {
		// these should all be numbers, not strings.
		_.each(
			[
				'restrictedUserLevel',
				'spec1-number',
				'spec2-number',
				'spec1-tag',
				'spec1-vocab',
				'type',
			],
			(a) => {
				data[a] = parseInt(data[a]);
				// Save some grief
				if (isNaN(data[a])) {
					data[a] = 0;
				}
			},
		);

		// Set attribute object type
		let objectTypes = _.csvToArray(data.objectTypes);
		// Defaults to `asset`
		if (!objectTypes || !objectTypes.length) {
			objectTypes = ['asset'];
		}

		// Clip only attributes are not mandatory
		if (!_.contains(objectTypes, 'asset')) {
			data.mandatory = false;
		}

		// These fields are applied the same way for all types:
		let parsed = _.extend({}, _.pick(data, 'type', 'restrictedUserLevel'), {
			name: data.name.trim(),
			facet: data.facet && this.supportsFacets(data.type),
			mandatory: data.mandatory ? 1 : 0,
			multivalue: data.multivalue,
			objectTypes: objectTypes,
			trackHistory: data.trackHistory && this.historyEnabled(),
		});

		// Adjust depending on type
		switch (data.type) {
			case AttributeType.TEXT:
				parsed.value = data['value-text'];
				break;

			case AttributeType.TEXTAREA:
				parsed.value = data['value-textarea'];
				break;

			case AttributeType.SELECT:
				parsed.spec1 = data['spec1-vocab'];
				break;

			case AttributeType.DATE:
				parsed.value = data['value-date'];
				break;

			case AttributeType.NUMBER:
				_.extend(parsed, {
					value: data['value-number'],
					spec1: data['spec1-number'],
					spec2: data['spec2-number'],
				});
				break;
			case AttributeType.SEMANTIC:
				parsed.semanticTypeId = parseInt(data.semanticTypeId);
				break;
			case AttributeType.TAG:
				parsed.spec1 = data['spec1-vocab'];
				// Intentionally piggy backing off of regular tag spec2 here
				parsed.spec2 = data['spec2-tag'] === true ? 1 : 0;
				break;
		}

		// We only allow certain edits to existing attributes
		if (!this.isNew()) {
			const updatableFields = [
				'name',
				'facet',
				'mandatory',
				'pulldownOptions',
				'restrictedUserLevel',
				'trackHistory',
				'value',
			];

			if ([AttributeType.TAG].includes(data.type)) {
				updatableFields.push('spec2');
			}

			parsed = _.pick.apply(_, [parsed].concat(updatableFields));
		} else {
			// we need to address the attribute template management altogether- safeguarding before save here for now
			parsed.spec1 =
				parsed.spec2 === ATTRIBUTE_NUMBER_SPEC_2_TYPE.UNLIMITED
					? 0
					: parsed.spec1;
		}

		// Set and save
		return this.save(parsed, _.extend({wait: true}, opts));
	}

	/**
	 * Parse in value for this attribute
	 * @method
	 * @param {mixed} value Value
	 * @return {mixed}
	 */
	parseInValue(value) {
		// Our asset beans still use csv
		// So gross
		if (
			this.isTrueMultivalue() &&
			value != null &&
			!_.isArray(value) &&
			value.includes('"')
		) {
			return _.csvToArray(value);
		}
		return value;
	}

	/**
	 * Parse out value for this attribute
	 * @method
	 * @param {mixed} value Value
	 * @return {mixed}
	 */
	parseOutValue(value) {
		// Not worth the effort
		if (_.isUndefined(value) || _.isNull(value) || value === '') {
			return value;
		}

		// True multivalue
		if (this.isTrueMultivalue()) {
			if (_.isObject(value)) {
				// Strip out any empty/null/undefined values
				// Cannot use _.compact since it strips `false` and `0`
				value = _.filter(
					_.values(value),
					(v) => v !== '' && !_.isNull(v) && !_.isUndefined(v),
				);
			} else if (_.isString(value)) {
				value = _.csvToArray(value);
			}
		} else if (this.isCSV && _.isString(value)) {
			value = _.csvToArray(value);
		}

		return value;
	}

	/**
	 * Are permissions for this resource enabled
	 * @override
	 * @return {boolean}
	 */
	permissionsEnabled() {
		return false;
	}

	/**
	 * Remove object type from attribute
	 * @method
	 * @param {string} objectType Object type (asset/clip)
	 * @type {object} opts Request options
	 */
	removeObjectType(objectType, opts) {
		const objectTypes = this.get('objectTypes');

		if (!_.contains(objectTypes, objectType)) {
			return new $.Deferred().resolve();
		}

		return this.save(
			{objectTypes: _.without(objectTypes, objectType)},
			{wait: true, ...opts},
		);
	}

	/**
	 * Test whether this attribute supports facets
	 * @method
	 * @param {number} type Attribute type
	 * @return {boolean} Supports facets
	 */
	supportsFacets(type) {
		return ATTRIBUTE_FACET_TYPES.includes(type ?? this.get('type'));
	}

	/**
	 * @method
	 * @param {boolean} on Toggle facet on
	 * @param {object} opts Options
	 */
	toggleMandatory(on, opts) {
		// Clip only attributes cannot be mandatory
		if (!this.isForObjectType('asset')) {
			on = false;
		}

		on = on != null ? on === true : !this.get('mandatory');

		// If it is the same there is nothing to do
		if (on === this.get('mandatory')) {
			return new $.Deferred().resolve();
		}

		return this.save({mandatory: on ? 1 : 0}, _.extend({wait: true}, opts));
	}

	/**
	 * @method
	 * @param {boolean} on Toggle facet on
	 * @param {object} opts Options
	 */
	toggleFacet(on, opts) {
		if (!ATTRIBUTE_FACET_TYPES.includes(this.get('type'))) {
			on = false;
		}

		on = on != null ? on === true : !this.get('facet');

		// If it is the same there is nothing to do
		if (on === this.get('facet')) {
			return new $.Deferred().resolve();
		}

		return this.save({facet: on}, _.extend({wait: true}, opts));
	}

	/**
	 * @method
	 * @return {boolean}
	 */
	trackingHistory() {
		return this.historyEnabled() && this.get('trackHistory');
	}

	/**
	 * @method
	 * @param {mixed} value Value
	 * @return {boolean} Valid
	 */
	validator(value) {
		return true;
	}

	/** @type {string} */
	static get __name__() {
		return 'Attribute';
	}
}
